import React, { Fragment, useEffect, useState, useCallback } from 'react';
import AdminPageLoader from './AdminPageLoader';
import axios from 'axios';
import { FaSearch, FaUsers, FaUser, FaPhone, FaMapMarkerAlt, FaEye } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function CustomerPage() {
    const [customers, setCustomers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 100,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getData = async (page = 1, search = '', append = false) => {
        if (page === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/userdata?page=${page}&limit=100&search=${search}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const users = response.data.users || [];
            if (append) {
                setCustomers(prev => [...prev, ...users]);
            } else {
                setCustomers(users);
            }
            setPagination({
                currentPage: page,
                totalPages: response.data.totalPages || 1,
                totalItems: response.data.totalUsers || users.length,
                itemsPerPage: 100,
                hasNextPage: page < (response.data.totalPages || 1),
                hasPrevPage: page > 1
            });
            setCurrentPage(page);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    getData(1, value, false);
                }, 500); // 500ms delay
            };
        })(),
        []
    );

    // Search customers with debouncing
    const handleSearchCustomer = (event) => {
        const search = event.target.value;
        setSearchTerm(search);
        debouncedSearch(search);
    };

    const handleViewDetails = (customer) => {
        setSelectedCustomer(customer);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedCustomer(null);
    };

    useEffect(() => {
        getData(1, '', false);
    }, []);

    // Scroll listener for infinite scroll
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150) {
                if (!loading && !loadingMore && pagination.hasNextPage) {
                    getData(currentPage + 1, searchTerm, true);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, loadingMore, currentPage, pagination.hasNextPage, searchTerm]);

    // Formats the address array correctly
    const formatAddress = (addressArray) => {
        if (!addressArray || addressArray.length === 0) {
            return "No Address";
        }
        return addressArray.map(addr =>
            `${addr.house || "N/A"}, ${addr.area || "N/A"}, ${addr.city || "N/A"}, ${addr.landmark ? `Landmark: ${addr.landmark}` : ""}`
        ).join("; ");
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">
                                    USER MANAGEMENT ({pagination.totalItems} TOTAL)
                                </h4>
                                <p className="text-muted p-0 m-0">Manage customer accounts and information</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Control Panel — cloned from Products page */}
                <div className="control-panel-bar">
                    <div className="control-panel-search">
                        <span
                            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                            style={{ pointerEvents: 'none' }}
                        >
                            <FaSearch style={{ fontSize: '14px' }} />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-5"
                            placeholder="Search by customer name, mobile, or address..."
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={searchTerm}
                            onChange={handleSearchCustomer}
                        />
                    </div>
                    <div className="control-panel-reset" style={{ marginLeft: 'auto' }}>
                        <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            Showing {customers.length} of {pagination.totalItems} customers
                        </small>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading customers..." />
                ) : (
                    <div className="row orders-table-row">
                        <div className="col-12">
                            {customers.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover orders-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '10%', minWidth: '60px' }}>#</th>
                                                <th style={{ width: '35%', minWidth: '220px' }}>Customer Info</th>
                                                <th style={{ width: '35%', minWidth: '220px' }}>Contact & Addresses</th>
                                                <th style={{ width: '15%', minWidth: '130px' }}>Created At</th>
                                                <th style={{ width: '10%', minWidth: '80px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customers.map((customer, index) => {
                                                const firstAddress = customer.address?.[0] || {};
                                                return (
                                                    <tr key={customer._id}>
                                                        <td>
                                                            <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                                                                {index + 1}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column align-items-start">
                                                                <strong style={{ color: '#1a237e', fontSize: '14px' }}>
                                                                    {firstAddress.name || customer.name || 'N/A'}
                                                                </strong>
                                                            </div>
                                                        </td>
                                                        <td>
                                                             <div className="d-flex flex-column align-items-start gap-1">
                                                                 {customer.deleted || (customer.mobile && customer.mobile.startsWith('deleted:')) ? (
                                                                     <>
                                                                         <span style={{ fontWeight: '600', color: '#dc2626', fontSize: '14px' }}>
                                                                             {customer.previousMobile || (customer.mobile?.startsWith('deleted:') ? customer.mobile.split(':')[1] : customer.mobile)}
                                                                         </span>
                                                                         <span className="badge bg-danger text-white" style={{ fontWeight: '600', fontSize: '11px', padding: '3px 6px', borderRadius: '4px' }}>
                                                                             Account Deleted
                                                                         </span>
                                                                     </>
                                                                 ) : (
                                                                     <>
                                                                         <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>
                                                                             {customer.mobile || firstAddress.mobile || 'N/A'}
                                                                         </span>
                                                                         <span className="badge bg-warning text-dark" style={{ fontWeight: '600', fontSize: '11px', padding: '3px 6px', borderRadius: '4px' }}>
                                                                             {customer.address?.length || 0} addresses
                                                                         </span>
                                                                     </>
                                                                 )}
                                                             </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column align-items-start">
                                                                <span style={{ fontWeight: '500', color: '#475569', fontSize: '13px' }}>
                                                                    {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <button 
                                                                    type="button"
                                                                    className="btn-action-square btn-action-view"
                                                                    title="View Details"
                                                                    onClick={() => handleViewDetails(customer)}
                                                                >
                                                                    <FaEye size={12} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* Infinite Scroll loading indicators */}
                                    {loadingMore && (
                                        <div className="text-center py-4 bg-white border-bottom">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Loading more...</span>
                                            </div>
                                            <span className="ms-2 text-muted" style={{ fontSize: '14px', fontWeight: 500 }}>Loading more customers...</span>
                                        </div>
                                    )}

                                    {!pagination.hasNextPage && customers.length > 0 && (
                                        <div className="text-center py-4 bg-white border-bottom text-muted" style={{ fontSize: '13px', fontWeight: 500 }}>
                                            Showing all {pagination.totalItems} customers.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaUsers className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No customers found matching your search.' : 'No customers found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Customer accounts will appear here.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Customer Details Modal overlay */}
            {showModal && selectedCustomer && (
                <div className="assign-modal-overlay" onClick={closeModal}>
                    <div className="assign-modal-content" style={{ width: '560px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        
                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaUser />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Customer Details
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Detailed profile and address book of the customer
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="assign-modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '20px' }}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Customer ID</label>
                                        <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', background: '#f8fafc', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', margin: 0 }}>
                                            {selectedCustomer._id}
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Name</label>
                                        <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', background: '#f8fafc', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', margin: 0 }}>
                                            {selectedCustomer.address?.[0]?.name || selectedCustomer.name || "N/A"}
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Main Mobile</label>
                                        <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', background: '#f8fafc', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FaPhone className="text-muted" size={12} />
                                            {selectedCustomer.previousMobile || (selectedCustomer.mobile?.startsWith('deleted:') ? selectedCustomer.mobile.split(':')[1] : selectedCustomer.mobile) || selectedCustomer.address?.[0]?.mobile || "N/A"}
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' }}>Registered Date</label>
                                        <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', background: '#f8fafc', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', margin: 0 }}>
                                            {selectedCustomer.createdAt ? new Date(selectedCustomer.createdAt).toLocaleString('en-GB') : "N/A"}
                                        </p>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>Address Book</label>
                                    <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                        {selectedCustomer.address && selectedCustomer.address.length > 0 ? (
                                            selectedCustomer.address.map((addr, index) => (
                                                <div key={index} className="border rounded-0 p-3 mb-2" style={{ background: '#ffffff', borderColor: '#cbd5e1' }}>
                                                    <small style={{ color: '#1a237e', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase' }}>Address #{index + 1}</small>
                                                    <p className="mb-1 fw-bold text-dark mt-1" style={{ fontSize: '13px' }}>
                                                        {addr.name || 'N/A'} - {addr.mobile || 'N/A'}
                                                    </p>
                                                    <p className="mb-1 text-muted" style={{ fontSize: '12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                        <FaMapMarkerAlt className="mt-1" size={11} style={{ flexShrink: 0 }} />
                                                        <span>{addr.house || "N/A"}, {addr.area || "N/A"}, {addr.city || "N/A"}</span>
                                                    </p>
                                                    {addr.landmark && (
                                                        <p className="mb-0 text-muted" style={{ fontSize: '11px', paddingLeft: '17px' }}>
                                                            Landmark: {addr.landmark}
                                                        </p>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted mb-0" style={{ fontSize: '13px', textAlign: 'center', padding: '20px' }}>No saved addresses found</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default CustomerPage;
